// @ts-check
import { gql } from '@apollo/client';
import { Box } from '.';

/**
 *
 * @param {object} props
 * @param {string} props.alt The alt text. Mandatory. If the image is only decorative, use an empty string, otherwise describe what is in the image.
 * @param {{ src: string, srcSet?: string, width: number, height: number }} [props.fixed] A fixed-width image, one that always has the same size.
 * @param {{ src: string, srcSet?: string, sizes: string, aspectRatio: number, width: number, height: number }} [props.fluid] A fluid image that adapts to the available space of the parent. You can create hybrid variants by using the maxWidth.
 * @param {React.ReactElement?} [props.fallback] A fallback if no image is available.
 * @param {() => void} [props.onError] Handles loading errors.
 * @returns
 */
export default function Image({ fixed, fluid, alt, fallback = null, ...rest }) {
  if (fixed) {
    // @ts-ignore
    return <img {...fixed} alt={alt} {...rest} />;
  }
  if (fluid) {
    const { aspectRatio, ...imgProps } = fluid;
    return (
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            paddingBottom: `${100 / aspectRatio}%`,
            maxWidth: imgProps.width,
          }}
        />
        <img
          {...imgProps}
          alt={alt}
          // @ts-ignore
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center center',
          }}
          {...rest}
        />
      </Box>
    );
  }
  return fallback;
}

Image.fixedImage = gql`
  fragment fixedImage on CloudinaryFixed {
    width
    height
    src
    srcSet
  }
`;
Image.fixedImage_noSrcSet = gql`
  fragment fixedImage_noSrcSet on CloudinaryFixed {
    width
    height
    src
  }
`;
Image.fluidImage = gql`
  fragment fluidImage on CloudinaryFluid {
    aspectRatio
    sizes
    src
    srcSet
    width
    height
  }
`;
